import { type FunctionComponent, lazy, Suspense, useEffect, useState } from 'react';

import { setTag, withProfiler } from '@sentry/react';
import { InsightsProvider } from 'contexts/insights';
import { KompisProvider } from 'contexts/kompis';
import GlobalStyles from 'GlobalStyles';
import useUpdateUserAgent from 'hooks/useUpdateUserAgent';
import { getDefaultPanel, getDefaultPanels } from 'products/panels';
import { configurationAction, type IPanelStyle } from 'redux-store/configuration/configuration.store';
import { useAppDispatch, useAppSelector } from 'redux-store/hooks';
import { productAction } from 'redux-store/products/products.store';
import { fetchProductsData } from 'redux-store/products/products.thunks';
import { getMandatoryProductsData } from 'redux-store/products/products.utils';
import {
  selectAppSettings,
  selectDefaultPanelStyles,
  selectDoorHeightOptionsAvailable,
  selectPanelPatternStyles,
  selectProducts,
  selectSelectedPanelStyles,
  selectShowErrorPage,
  selectTranslations,
} from 'redux-store/rootReducer';
import { fetchSettings } from 'redux-store/settings/settings.thunks';
import store from 'redux-store/store';
import { fetchTranslations } from 'redux-store/translations/translations.thunks';
import { AppStateEnum, type IShoppingProductsMap, QUERY_PARAMS, SentryTags } from 'utils/constants';
import getQueryParam from 'utils/getQueryParam';
import { logSkyttaAppVersion } from 'utils/logSkytta';
import { isProduction } from 'utils/platform';
import { supportsWebgl } from 'utils/supportsWebgl';
import { supportsWebp } from 'utils/supportsWebp';

// Core is already initiated in their providers and since we import them,
// the code for coreDefaultSetup is already initiated.
// We need to postpone it to make sure the critical dexf data is obtained.
const App = lazy(() => {
  return import('App');
});

const ErrorPage = lazy(() => {
  return import('views/ErrorPage');
});

const handleShowErrorPage = async (hasMandatoryProductsMissing: boolean) => {
  const unsafeMode = getQueryParam(QUERY_PARAMS.UNSAFE) === 'true' && !isProduction();
  const showErrorPageForInvalidProducts = hasMandatoryProductsMissing && !unsafeMode;

  const webpBrowser = await supportsWebp();
  const webglBrowser = supportsWebgl();
  const showErrorPageForUnsupportedBrowser = !webpBrowser || !webglBrowser;

  store.dispatch(
    configurationAction.setShowErrorPage(showErrorPageForInvalidProducts || showErrorPageForUnsupportedBrowser),
  );
};

logSkyttaAppVersion();

const PreApp: FunctionComponent = () => {
  const [appReady, setAppReady] = useState(false);
  const [initError, setInitError] = useState<'string' | undefined>();
  const [processedData, setProcessedData] = useState(false);
  const dispatch = useAppDispatch();
  const errorPageVisible = useAppSelector(selectShowErrorPage);
  const translations = useAppSelector(selectTranslations);
  const settings = useAppSelector(selectAppSettings);
  const allProductData = useAppSelector(selectProducts);
  const doorHeightOptionsAvailable = useAppSelector(selectDoorHeightOptionsAvailable);
  const selectedPanelStyles = useAppSelector(selectSelectedPanelStyles);
  const panelPatternStyles = useAppSelector(selectPanelPatternStyles);
  const defaultPanelStyles = useAppSelector(selectDefaultPanelStyles);

  useUpdateUserAgent();

  const processData = async (productData: IShoppingProductsMap) => {
    const { hasMandatoryProductsMissing, missingSkyttaProducts, doorHeightOptionsAvailable, isThreeTrackAvailable } =
      getMandatoryProductsData(productData);
    await handleShowErrorPage(hasMandatoryProductsMissing);

    dispatch(productAction.setMissingSkyttaProducts(missingSkyttaProducts));
    dispatch(productAction.setDoorHeightOptionsAvailable(doorHeightOptionsAvailable));
    dispatch(productAction.setIsThreeTrackAvailable(isThreeTrackAvailable));

    const defaultPanels = getDefaultPanels(doorHeightOptionsAvailable);
    const validPanel = getDefaultPanel(productData, defaultPanels);

    const index = defaultPanels.indexOf(validPanel.product.itemId.split('ART-')[1]);
    const defaultPanel: IPanelStyle = { index, isFlipped: false };

    dispatch(configurationAction.setDefaultPanelStyles([defaultPanel]));
    dispatch(configurationAction.setSelectedPanelStyles([[defaultPanel]]));
    dispatch(
      configurationAction.setSelectedPanelPatternStyles({
        primaryPanelStyle: defaultPanel,
      }),
    );
  };

  const validatePayload = async (result: Promise<any>) => {
    const awaitedResult = await result;
    if (awaitedResult?.payload) {
      return result;
    }

    const message = awaitedResult?.error?.message;
    if (message) {
      dispatch(configurationAction.setShowAppLoadingScreen(false));
      setInitError(message);
    }
    return false;
  };

  useEffect(() => {
    (async () => {
      /**
       * We had a issue on sentry where users got crashes, it was hard to find but apparently using setLocalization with reducer instead of fetchLocalization with extra reducer seems to be the issue here.
       * We can still not find the solution to it.
       * If you're trying to refactor it, please test it on sentry with a silent release and watch for connectionProblemException.
       */

      // Waiting for settings is needed to get correct endpoints for fetching product data
      const settingsResult = await validatePayload(dispatch(fetchSettings()));
      if (!settingsResult) {
        return;
      }
      const translationsResult = await validatePayload(dispatch(fetchTranslations()));
      if (!translationsResult) {
        return;
      }
      validatePayload(dispatch(fetchProductsData()));
    })();
  }, []);

  /**
   * Process solution data after critical data has been loaded.
   */
  useEffect(() => {
    (async () => {
      if (!appReady && !processedData) {
        const hasSettings = settings && Object.keys(settings).length;
        const hasTranslations = translations && Object.keys(translations).length;
        const hasProducts = allProductData && Object.keys(allProductData).length;

        if (hasSettings && hasTranslations && hasProducts) {
          await processData(allProductData);
          setProcessedData(true);
        }
      }
    })();
  }, [settings, translations, allProductData]);

  /**
   * Set appReady if all needed data is available
   */
  useEffect(() => {
    const hasPanelPatternStyles = panelPatternStyles && Object.keys(panelPatternStyles).length;
    const hasDoorHeightOptionsAvailable = doorHeightOptionsAvailable.length;
    const hasSelectedPanelStyles = selectedPanelStyles.length;
    const hasDefaultPanelStyles = defaultPanelStyles.length;

    (async () => {
      if (
        !appReady &&
        processedData &&
        hasDoorHeightOptionsAvailable &&
        hasSelectedPanelStyles &&
        hasPanelPatternStyles &&
        hasDefaultPanelStyles
      ) {
        setTag(SentryTags.appState, AppStateEnum.Ready);

        setAppReady(true);
      }
    })();
  }, [panelPatternStyles, doorHeightOptionsAvailable, selectedPanelStyles, defaultPanelStyles, processedData]);

  if (initError) {
    return <h1>{initError}</h1>;
  }

  if (errorPageVisible) {
    return (
      <KompisProvider>
        <InsightsProvider>
          <GlobalStyles />
          <Suspense fallback={null}>
            <ErrorPage />
          </Suspense>
        </InsightsProvider>
      </KompisProvider>
    );
  }

  if (!appReady) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <App />
    </Suspense>
  );
};

export default withProfiler(PreApp, { includeUpdates: false });
